import React, { useMemo, useState, Children } from 'react';
import { IconArrowLeft, IconSquareCheck, IconSquareCheckFilled, IconMenu2, IconCircleCheck, IconClock, IconTrendingUp, IconSearch, IconX, IconSquare } from '@tabler/icons-react';
import { ChecklistCard, ChecklistItem } from '../components/ChecklistCard';
// Children data
interface Child {
  id: string;
  name: string;
  avatar: string;
}
const familyChildren: Child[] = [{
  id: 'child-1',
  name: 'Sofia',
  avatar: 'https://cdn.magicpatterns.com/uploads/c3jxasi4fCCZ93c9aemubP/adele-shafiee-KCvjle4_4ps-unsplash.jpg'
}, {
  id: 'child-2',
  name: 'Marco',
  avatar: 'https://cdn.magicpatterns.com/uploads/1EXQ4FkeqG6UuqJKWikck9/tatev-arsh-ZtB5Haz58os-unsplash.jpg'
}];
interface Category {
  id: string;
  name: string;
  color: string;
  totalTasks: number;
  completedTasks: number;
  checklists: Checklist[];
}
interface Checklist {
  id: string;
  title: string;
  items: ChecklistItem[];
  categoryId: string;
}
interface SearchResult {
  type: 'task' | 'checklist';
  checklistId: string;
  checklistTitle: string;
  categoryColor: string;
  taskId?: string;
  taskText?: string;
  taskCompleted?: boolean;
  categoryName: string;
}
interface ActivitiesPageProps {}
export function ActivitiesPage({}: ActivitiesPageProps) {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChildFilter, setSelectedChildFilter] = useState<string | null>(null);
  // Mock data - categorie con checklist
  const categories: Category[] = [{
    id: 'school',
    name: 'Scuola',
    color: '#AEDAFF',
    totalTasks: 8,
    completedTasks: 3,
    checklists: [{
      id: 'c1',
      title: 'Iscrizione Scuola Elementare - Sofia',
      categoryId: 'school',
      items: [{
        id: '1',
        text: 'Registrazione portale MIUR',
        completed: true
      }, {
        id: '2',
        text: 'Codice meccanografico scuola Manzoni',
        completed: true
      }, {
        id: '3',
        text: 'Compilare modulo iscrizione online',
        completed: false
      }, {
        id: '4',
        text: 'Allegare documento identità',
        completed: false
      }]
    }, {
      id: 'c2',
      title: 'Materiale Scolastico - Marco',
      categoryId: 'school',
      items: [{
        id: '5',
        text: 'Acquistare zaino nuovo',
        completed: true
      }, {
        id: '6',
        text: 'Comprare quaderni e penne',
        completed: false
      }, {
        id: '7',
        text: 'Etichettare tutto il materiale',
        completed: false
      }, {
        id: '8',
        text: 'Preparare astuccio completo',
        completed: false
      }]
    }]
  }, {
    id: 'bureaucracy',
    name: 'Burocrazia',
    color: '#FAD88A',
    totalTasks: 6,
    completedTasks: 2,
    checklists: [{
      id: 'c3',
      title: 'Rinnovo Passaporto - Marco',
      categoryId: 'bureaucracy',
      items: [{
        id: '9',
        text: 'Prenotare appuntamento in Questura',
        completed: true
      }, {
        id: '10',
        text: 'Pagare marca da bollo €73.50',
        completed: true
      }, {
        id: '11',
        text: 'Portare 2 fototessere recenti',
        completed: false
      }, {
        id: '12',
        text: 'Documento identità genitore',
        completed: false
      }]
    }, {
      id: 'c4',
      title: 'Carta Identità - Sofia',
      categoryId: 'bureaucracy',
      items: [{
        id: '13',
        text: 'Prenotare appuntamento anagrafe',
        completed: false
      }, {
        id: '14',
        text: 'Portare certificato nascita',
        completed: false
      }]
    }]
  }, {
    id: 'health',
    name: 'Salute',
    color: '#B0EC9C',
    totalTasks: 4,
    completedTasks: 1,
    checklists: [{
      id: 'c5',
      title: 'Cambio Pediatra - Sofia',
      categoryId: 'health',
      items: [{
        id: '15',
        text: 'Prenotare appuntamento ASL per cambio',
        completed: true
      }, {
        id: '16',
        text: 'Portare tessera sanitaria',
        completed: false
      }, {
        id: '17',
        text: 'Scegliere nuovo pediatra dalla lista',
        completed: false
      }]
    }, {
      id: 'c6',
      title: 'Visita Oculistica - Marco',
      categoryId: 'health',
      items: [{
        id: '18',
        text: 'Prenotare visita oculistica',
        completed: false
      }]
    }]
  }, {
    id: 'sports',
    name: 'Sport',
    color: '#DDD6FE',
    totalTasks: 3,
    completedTasks: 0,
    checklists: [{
      id: 'c7',
      title: 'Iscrizione Corso Nuoto - Marco',
      categoryId: 'sports',
      items: [{
        id: '19',
        text: 'Certificato medico sportivo non agonistico',
        completed: false
      }, {
        id: '20',
        text: 'Pagare quota iscrizione €150',
        completed: false
      }, {
        id: '21',
        text: 'Comprare cuffia e costume',
        completed: false
      }]
    }]
  }];
  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  // Filter categories by selected child
  const filteredCategories = selectedChildFilter ? categories.map(category => ({
    ...category,
    checklists: category.checklists.filter(checklist => checklist.title.toLowerCase().includes(familyChildren.find(c => c.id === selectedChildFilter)?.name.toLowerCase() || ''))
  })).filter(category => category.checklists.length > 0).map(category => ({
    ...category,
    totalTasks: category.checklists.reduce((sum, cl) => sum + cl.items.length, 0),
    completedTasks: category.checklists.reduce((sum, cl) => sum + cl.items.filter(i => i.completed).length, 0)
  })) : categories;
  // Search functionality
  const searchResults: SearchResult[] = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();
    const results: SearchResult[] = [];
    const searchableCategories = selectedChildFilter ? filteredCategories : categories;
    searchableCategories.forEach(category => {
      category.checklists.forEach(checklist => {
        // Search in checklist title
        if (checklist.title.toLowerCase().includes(query)) {
          results.push({
            type: 'checklist',
            checklistId: checklist.id,
            checklistTitle: checklist.title,
            categoryColor: category.color,
            categoryName: category.name
          });
        }
        // Search in task text
        checklist.items.forEach(item => {
          if (item.text.toLowerCase().includes(query)) {
            results.push({
              type: 'task',
              checklistId: checklist.id,
              checklistTitle: checklist.title,
              categoryColor: category.color,
              taskId: item.id,
              taskText: item.text,
              taskCompleted: item.completed,
              categoryName: category.name
            });
          }
        });
      });
    });
    return results;
  }, [searchQuery, categories, selectedChildFilter, filteredCategories]);
  const handleToggleTask = (checklistId: string, itemId: string) => {
    // TODO: Implement task toggle
    console.log('Toggle task:', checklistId, itemId);
  };
  const handleSearchResultClick = (result: SearchResult) => {
    // Find the category
    const category = categories.find(c => c.checklists.some(ch => ch.id === result.checklistId));
    if (category) {
      setSelectedCategory(category.id);
      setSearchQuery('');
    }
  };
  const handleClearSearch = () => {
    setSearchQuery('');
  };
  // Calculate overall stats
  const totalTasks = filteredCategories.reduce((sum, cat) => sum + cat.totalTasks, 0);
  const completedTasks = filteredCategories.reduce((sum, cat) => sum + cat.completedTasks, 0);
  const completionPercentage = totalTasks > 0 ? Math.round(completedTasks / totalTasks * 100) : 0;
  const pendingTasks = totalTasks - completedTasks;
  const showSearchResults = searchQuery.trim().length > 0;
  return <div className="flex flex-col h-full" data-id="element-460">
      {selectedCategory ?
    // Category detail: sub-navigation header
    <div className="px-4 pt-3 pb-3" data-id="element-461">
          <div className="flex items-center gap-3" data-id="element-462">
            <button onClick={() => setSelectedCategory(null)} className="flex items-center gap-1 py-2 pr-2 transition-opacity hover:opacity-70" aria-label="Torna alle categorie" data-id="element-463">
              <IconArrowLeft className="w-5 h-5 text-yeppy-text" stroke={2} data-id="element-464" />
              <span className="font-poppins text-base text-yeppy-text font-medium" data-id="element-465">
                Attività
              </span>
            </button>
            <div className="flex-1 flex justify-end" data-id="element-466">
              <div className="text-right" data-id="element-467">
                <h2 className="font-rubik font-semibold text-base text-yeppy-text" data-id="element-468">
                  {selectedCategoryData?.name}
                </h2>
                <p className="font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-469">
                  {selectedCategoryData?.completedTasks} di{' '}
                  {selectedCategoryData?.totalTasks} completate
                </p>
              </div>
            </div>
          </div>
        </div> :
    // Overview: child filter + search (fixed, non-scrolling)
    <div className="px-4 pb-3" data-id="element-470">
          {/* Children selector */}
          <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide" data-id="element-471">
            {/* "Tutti" pill */}
            <button onClick={() => setSelectedChildFilter(null)} className={`flex items-center gap-2 px-4 py-2 rounded-full font-poppins text-sm font-medium transition-all flex-shrink-0 ${selectedChildFilter === null ? 'bg-yeppy-text text-white' : 'bg-white border border-yeppy-text border-opacity-15 text-yeppy-text'}`} data-id="element-472">
              Tutti
            </button>

            {/* Child pills */}
            {familyChildren.map(child => <button key={child.id} onClick={() => setSelectedChildFilter(selectedChildFilter === child.id ? null : child.id)} className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-poppins text-sm font-medium transition-all flex-shrink-0 ${selectedChildFilter === child.id ? 'bg-yeppy-text text-white' : 'bg-white border border-yeppy-text border-opacity-15 text-yeppy-text'}`} data-id="element-473">
                <img src={child.avatar} alt={child.name} className={`w-6 h-6 rounded-full object-cover flex-shrink-0 ${selectedChildFilter === child.id ? 'border-2 border-white' : 'border border-yeppy-text border-opacity-10'}`} data-id="element-474" />
                <span data-id="element-475">{child.name}</span>
              </button>)}
          </div>

          {/* Search bar */}
          <div className="relative" data-id="element-476">
            <IconSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-yeppy-text text-opacity-40 pointer-events-none z-10" stroke={1.5} data-id="element-477" />
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca task o checklist..." className="w-full pl-12 pr-10 py-3 rounded-2xl font-poppins text-sm text-yeppy-text bg-white border border-yeppy-text border-opacity-15 focus:outline-none focus:border-yeppy-text focus:border-opacity-30 transition-colors placeholder-yeppy-text placeholder-opacity-40" data-id="element-478" />
            {searchQuery && <button onClick={handleClearSearch} className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-yeppy-text bg-opacity-10 flex items-center justify-center transition-colors" aria-label="Cancella ricerca" data-id="element-479">
                <IconX className="w-4 h-4 text-yeppy-text" stroke={2.5} data-id="element-480" />
              </button>}
          </div>
        </div>}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 py-4" data-id="element-481">
        {!selectedCategory ? <>
            {/* KPI Widgets - hide when searching */}
            {!showSearchResults && <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-482">
                {/* Completate */}
                <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl p-3" data-id="element-483">
                  <p className="text-lg text-yeppy-text mb-0.5" data-id="element-484">
                    {completedTasks}
                  </p>
                  <p className="text-xs text-yeppy-text text-opacity-60" data-id="element-485">
                    Completate
                  </p>
                </div>

                {/* Da fare */}
                <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl p-3" data-id="element-486">
                  <p className="text-lg text-yeppy-text mb-0.5" data-id="element-487">
                    {pendingTasks}
                  </p>
                  <p className="text-xs text-yeppy-text text-opacity-60" data-id="element-488">
                    Da fare
                  </p>
                </div>

                {/* Percentuale */}
                <div className="bg-white border border-yeppy-text border-opacity-10 rounded-2xl p-3" data-id="element-489">
                  <p className="text-lg text-yeppy-text mb-0.5" data-id="element-490">
                    {completionPercentage}%
                  </p>
                  <p className="text-xs text-yeppy-text text-opacity-60" data-id="element-491">
                    Progresso
                  </p>
                </div>
              </div>}

            {showSearchResults ?
        // Search results view
        searchResults.length > 0 ? <div className="space-y-3" data-id="element-492">
                  {searchResults.map((result, index) => <button key={`${result.type}-${result.checklistId}-${result.taskId || 'checklist'}-${index}`} onClick={() => handleSearchResultClick(result)} className={`w-full text-left transition-all ${result.type === 'checklist' ? 'bg-white border-2 rounded-2xl p-4' : 'bg-white border border-yeppy-text border-opacity-10 rounded-xl p-3'}`} style={result.type === 'checklist' ? {
            borderColor: result.categoryColor,
            borderLeftWidth: '6px'
          } : undefined} data-id="element-493">
                      {result.type === 'checklist' ? <div className="flex items-start gap-4" data-id="element-494">
                          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: `${result.categoryColor}33`
              }} data-id="element-495">
                            <IconSquareCheck className="w-6 h-6" stroke={2.5} style={{
                  color: result.categoryColor
                }} data-id="element-496" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-497">
                            <h3 className="font-rubik font-semibold text-base text-yeppy-text mb-2" data-id="element-498">
                              {result.checklistTitle}
                            </h3>
                            <div className="flex items-center gap-2" data-id="element-499">
                              <span className="inline-block px-3 py-1 rounded-full text-xs font-poppins font-medium" style={{
                    backgroundColor: `${result.categoryColor}33`,
                    color: '#1D2A36'
                  }} data-id="element-500">
                                {result.categoryName}
                              </span>
                              <span className="text-xs font-poppins text-yeppy-text text-opacity-50 uppercase tracking-wide" data-id="element-501">
                                Checklist
                              </span>
                            </div>
                          </div>
                        </div> : <div className="flex items-center gap-3" data-id="element-502">
                          <div className="flex items-center justify-center flex-shrink-0" data-id="element-503">
                            {result.taskCompleted ? <IconSquareCheckFilled className="w-5 h-5" style={{
                  color: result.categoryColor
                }} data-id="element-504" /> : <IconSquare className="w-5 h-5" stroke={1.5} style={{
                  color: result.categoryColor
                }} data-id="element-505" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-506">
                            <p className={`font-poppins text-sm mb-1 ${result.taskCompleted ? 'text-yeppy-text text-opacity-50 line-through' : 'text-yeppy-text'}`} data-id="element-507">
                              {result.taskText}
                            </p>
                            <div className="flex items-center gap-1.5" data-id="element-508">
                              <span className="text-xs font-poppins text-yeppy-text text-opacity-40" data-id="element-509">
                                da
                              </span>
                              <span className="text-xs font-poppins text-yeppy-text text-opacity-60 font-medium" data-id="element-510">
                                {result.checklistTitle}
                              </span>
                              <span className="text-xs font-poppins text-yeppy-text text-opacity-40" data-id="element-511">
                                ·
                              </span>
                              <span className="text-xs font-poppins text-yeppy-text text-opacity-60" data-id="element-512">
                                {result.categoryName}
                              </span>
                            </div>
                          </div>
                        </div>}
                    </button>)}
                </div> : <div className="flex flex-col items-center justify-center h-full text-center px-4" data-id="element-513">
                  <IconSearch className="w-16 h-16 text-yeppy-text text-opacity-30 mb-4" stroke={1.5} data-id="element-514" />
                  <h3 className="font-rubik font-semibold text-base text-yeppy-text mb-2" data-id="element-515">
                    Nessun risultato
                  </h3>
                  <p className="font-poppins text-sm text-yeppy-text text-opacity-60 max-w-xs" data-id="element-516">
                    Non ho trovato task o checklist che corrispondono alla tua
                    ricerca.
                  </p>
                </div> :
        // Categories overview
        <div className="space-y-3" data-id="element-517">
                {filteredCategories.map(category => {
            const percentage = category.totalTasks > 0 ? Math.round(category.completedTasks / category.totalTasks * 100) : 0;
            return <button key={category.id} onClick={() => setSelectedCategory(category.id)} className="w-full bg-white border border-yeppy-text border-opacity-10 rounded-2xl px-4 py-3 transition-all text-left" data-id="element-518">
                      <div className="flex items-start gap-3 mb-3" data-id="element-519">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{
                  backgroundColor: `${category.color}33`
                }} data-id="element-520">
                          <IconSquareCheck className="w-5 h-5" stroke={2.5} style={{
                    color: category.color
                  }} data-id="element-521" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-522">
                          <h3 className="font-rubik text-base text-yeppy-text mb-1 font-[500]" data-id="element-523">
                            {category.name}
                          </h3>
                          <p className="font-poppins text-sm text-yeppy-text text-opacity-60" data-id="element-524">
                            {category.checklists.length}{' '}
                            {category.checklists.length === 1 ? 'checklist' : 'checklist'}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-525">
                          <p className="font-rubik font-semibold text-base text-yeppy-text" data-id="element-526">
                            {category.completedTasks}/{category.totalTasks}
                          </p>
                          <p className="font-poppins text-xs text-yeppy-text text-opacity-60" data-id="element-527">
                            {percentage}%
                          </p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full h-1.5 bg-yeppy-bg rounded-full overflow-hidden" data-id="element-528">
                        <div className="h-full rounded-full transition-all duration-300" style={{
                  width: `${percentage}%`,
                  backgroundColor: category.color
                }} data-id="element-529" />
                      </div>
                    </button>;
          })}

                {/* Empty state when filtering */}
                {filteredCategories.length === 0 && selectedChildFilter && <div className="flex flex-col items-center justify-center py-12 text-center px-4" data-id="element-530">
                    <img src={familyChildren.find(c => c.id === selectedChildFilter)?.avatar} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-yeppy-text border-opacity-10 mb-4 opacity-50" data-id="element-531" />
                    <h3 className="font-rubik font-semibold text-base text-yeppy-text mb-2" data-id="element-532">
                      Nessuna attività
                    </h3>
                    <p className="font-poppins text-sm text-yeppy-text text-opacity-60 max-w-xs" data-id="element-533">
                      Non ci sono attività per{' '}
                      {familyChildren.find(c => c.id === selectedChildFilter)?.name}{' '}
                      al momento.
                    </p>
                  </div>}
              </div>}
          </> :
      // Category detail - checklists with accordion
      <div className="space-y-3" data-id="element-534">
            {selectedCategoryData?.checklists.map(checklist => <ChecklistCard key={checklist.id} title={checklist.title} items={checklist.items} categoryColor={selectedCategoryData.color} defaultExpanded={true} onToggleItem={itemId => handleToggleTask(checklist.id, itemId)} data-id="element-535" />)}
          </div>}
      </div>
    </div>;
}